import React, { useEffect, useState } from "react";

import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";

import { createStyles, makeStyles } from "@mui/styles";

import JobService from "../../service/job";
import { ConfirmPopup } from "../Common/Common";
import { showToast } from "../../store/actions";

const useStyles = makeStyles((theme) =>
  createStyles({
    tr: {
      backgroundColor: "#ffffff",
      "&:hover": {
        backgroundColor: " rgba(0, 0, 0, 0.04)",
      },
      "& td, & th": {
        wordBreak: "break-word",
      },
    },
    TableHead: {
      fontWeight: 600,
      "& span.Mui-active": {
        background: "transparent",
        color: "inherit",
      },
      "& span": {
        "&:hover": {
          background: "transparent",
          color: "rgba(0, 0, 0, 0.6) !important",
        },
      },
    },
  })
);

const headCells = [
  {
    id: "Job_id",
    numeric: false,
    disablePadding: false,
    label: "PO #",
    width: 150,
  },
  {
    id: "CustomerName",
    numeric: false,
    disablePadding: false,
    label: "Customer Name",
    width: 150,
  },
  {
    id: "Name",
    numeric: false,
    disablePadding: false,
    label: "Job Name",
    width: 200,
  },
  {
    id: "ArtworkStatus",
    numeric: false,
    disablePadding: false,
    label: "Artwork Status",
    width: 110,
  },
  {
    id: "ImageFileStatus",
    numeric: false,
    disablePadding: false,
    label: "Sample Status",
    width: 110,
  },
  {
    id: "Status",
    numeric: false,
    disablePadding: false,
    label: "Status",
    width: 130,
  },
  {
    id: "EmployeeName",
    numeric: false,
    disablePadding: false,
    label: "Employee Name",
    width: 150,
  },
  {
    id: "SupplierName",
    numeric: false,
    disablePadding: false,
    label: "Supplier Name",
    width: 150,
  },
  {
    id: "Actions",
    numeric: false,
    disablePadding: false,
    label: "Actions",
    width: 100,
  },
];

const tableWidths = headCells.map((x) => x.width);

function DataTableHead({ user, isSupplierDashboard }) {
  const classes = useStyles();

  return (
    <TableHead>
      <TableRow>
        {headCells
          .filter(
            (row) =>
              (user.Role_id == 1 || !["CustomerName", "EmployeeName"].includes(row.id)) &&
              (!isSupplierDashboard || row.id !== "CustomerName") &&
              ((user.Role_id == 1 || user.Role_id == 2) ||
                !["SupplierName"].includes(row.id)) &&
              (isSupplierDashboard || row.id !== "SupplierName") && // Hide SupplierName on Main Dashboard
              (user.Role_id === 1 || !["Actions"].includes(row.id)) // Hide Actions for all except Admin
          )
          .map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              style={{
                width: headCell.width,
                ...(headCell.id === "Job_id" && {
                  position: "sticky",
                  left: 0,
                  zIndex: 900,
                }),
              }}
              className={classes.TableHead}
            >
              {headCell.label}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}

const Last20Jobs = ({ user, isSupplierDashboard, filters }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [jobId, setJobId] = useState(0);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [loadingDeletePopup, setLoadingDeletePopup] = useState(false);

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const loadData = (currentFilters) => {
    setLoading(true);
    const jobService = new JobService();

    jobService
      .getAllJobs({
        limit: rowsPerPage,
        page: page,
        ...currentFilters,
        orderBy: "Job_id",
        order: "desc",
        ...(user && user.Role_id == 2 ? { employee: user.User_id, isSupplierDashboard: isSupplierDashboard } : {}),
        ...(user && user.Role_id == 1 ? { isSupplierDashboard: isSupplierDashboard } : {}),
        ...(user && user.Role_id == 3 ? { customer: user.User_id } : {}),
        ...(user && user.Role_id == 4 ? { supplier: user.User_id } : {}),
      })
      .then((result) => {
        if (result.success && result.data) {
          setData(result.data.jobList);
          setTotalRecords(result.data.count || 0);
        } else {
          setData([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadData(filters);
  }, [isSupplierDashboard, page, rowsPerPage, filters]);

  // Function for Delete Job
  const deleteJob = async (jobIdToDelete) => {
    setLoadingDeletePopup(true);

    const jobService = new JobService();
    const result = await jobService.deleteJob(jobIdToDelete);

    if (result.success) {
      loadData(filters); // Reload data

      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
    }

    setOpenDeletePopup(false);
    setLoadingDeletePopup(false);
  };

  // Open Job Delete Confirm Popup
  const DeletePopup = ({ id }) => (
    <ConfirmPopup
      configData={{
        popupTitle: "Delete Job",
        popupMessage: "Are you sure you want to delete?",
        handleCancel: () => setOpenDeletePopup(false),
        handleClick: () => deleteJob(id),
        loadingForConfirm: loadingDeletePopup,
      }}
    />
  );

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: `100px`,
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (data.length === 0)
    return (
      <Paper
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: `60px`,
        }}
      >
        No jobs available currently
      </Paper>
    );

  return (
    <>
      <TableContainer
        sx={{
          // maxHeight: `${tableHeight}px`,
          ...(data.length === 0
            ? {
              overflow: "hidden",
            }
            : {}),
        }}
      >
        <Table stickyHeader sx={{ width: "100%" }} size="small">
          <DataTableHead user={user} isSupplierDashboard={isSupplierDashboard} />
          {data.length ? (
            <TableBody>
              {data.map((row, i) => {
                return (
                  <TableRow tabIndex={-1} key={row.Job_id} className={classes.tr}>
                    <TableCell
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 800,
                        minWidth: tableWidths[0],
                      }}
                      className={classes.td}
                    >
                      <Button
                        color="info"
                        size="small"
                        component={RouterLink}
                        style={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          display: "inline-block",
                          wordBreak: "break-word",
                          verticalAlign: "middle",
                        }}
                        to={`../jobs/${row.Job_id}`}
                        state={{ isSupplierDashboard: !!isSupplierDashboard }}
                      >
                        PO {row.ProductLink.startsWith("#") ? row.ProductLink : "#" + row.ProductLink}
                      </Button>
                    </TableCell>
                    {user.Role_id == 1 && !isSupplierDashboard && (
                      <TableCell style={{ minWidth: tableWidths[1] }} component="th" scope="row" padding="normal">
                        {row.CustomerName}
                      </TableCell>
                    )}
                    <TableCell style={{ minWidth: tableWidths[2] }}>{row.Name}</TableCell>
                    <TableCell style={{ minWidth: tableWidths[3] }}>{row.ArtworkStatus}</TableCell>
                    <TableCell style={{ minWidth: tableWidths[4] }}>{row.ImageFileStatus}</TableCell>
                    <TableCell style={{ minWidth: tableWidths[5] }}>{row.StatusText}</TableCell>
                    {user.Role_id == 1 && (
                      <TableCell style={{ minWidth: tableWidths[6] }}>{row.EmployeeName}</TableCell>
                    )}
                    {(user.Role_id == 1 || user.Role_id == 2) && !!isSupplierDashboard && (
                      <TableCell style={{ minWidth: tableWidths[7] }}>{row.SupplierName}</TableCell>
                    )}
                    {/* Display Delete button for Admin */}
                    {user.Role_id === 1 && (
                      <TableCell style={{ minWidth: tableWidths[8] }}>
                        <Tooltip
                          title="Delete Job"
                          className="btn btn-icon btn-danger-light"
                          style={{ verticalAlign: "middle" }}
                        >
                          <IconButton
                            color="error"
                            onClick={() => {
                              setJobId(row.Job_id);
                              setOpenDeletePopup(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : (
            <TableBody></TableBody>
          )}
        </Table>
      </TableContainer>
      {totalRecords > 0 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id="rows-per-page-label">Rows</InputLabel>
            <Select labelId="rows-per-page-label" value={rowsPerPage} label="Rows" onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <Pagination
            count={Math.ceil(totalRecords / rowsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            sx={{ mt: { xs: 2, md: 0 } }}
          />
        </Box>
      )}
      {openDeletePopup && <DeletePopup id={jobId} />}
    </>
  );
};

export default Last20Jobs;
